import React, { createContext, useContext, useState, useEffect } from 'react';
import SharingService from '../services/SharingService';
import { useFamilyContext } from './FamilyContext';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const SharingContext = createContext();

export const useSharingContext = () => {
  const context = useContext(SharingContext);
  if (!context) {
    throw new Error('useSharingContext must be used within a SharingProvider');
  }
  return context;
};

export const SharingProvider = ({ children }) => {
  const { currentUser, familyMembers } = useFamilyContext();
  const [sharedItems, setSharedItems] = useState({});
  const [parentSharedItems, setParentSharedItems] = useState([]);
  const [sharingPermissions, setSharingPermissions] = useState({});
  const [parentAccess, setParentAccess] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isParent, setIsParent] = useState(false);

  // Determine if current user is a parent
  useEffect(() => {
    if (currentUser) {
      setIsParent(currentUser.role === 'parent');
    }
  }, [currentUser]);

  // Load initial data
  useEffect(() => {
    if (currentUser) {
      loadSharingData();
      loadNotifications();
    }
  }, [currentUser, isParent]);

  const loadSharingData = async () => {
    try {
      setLoading(true);

      if (isParent) {
        // Load items shared with parent
        const items = await SharingService.getSharedItemsForParent();
        setParentSharedItems(items);
      } else {
        // Load student's shared items and permissions
        const [items, perms] = await Promise.all([
          SharingService.getSharedItemsByStudent(),
          SharingService.getSharingPermissions()
        ]);
        setSharedItems(items);
        setSharingPermissions(perms.permissions);
        setParentAccess(perms.parentAccess);
      }
    } catch (error) {
      console.error('Error loading sharing data:', error);
      toast.error('Failed to load sharing data');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const notifs = await SharingService.getNotifications();
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Share an item
  const shareItem = async (itemType, itemId, recipients, options = {}) => {
    try {
      const result = await SharingService.shareItem(
        itemType,
        itemId,
        recipients,
        options
      );

      toast.success('Item shared successfully');
      await loadSharingData();
      return result;
    } catch (error) {
      console.error('Error sharing item:', error);
      toast.error('Failed to share item');
      throw error;
    }
  };

  // Revoke sharing
  const revokeSharing = async (itemType, itemId, recipientId = null) => {
    try {
      await SharingService.revokeSharing(itemType, itemId, recipientId);
      toast.success('Sharing revoked');
      await loadSharingData();
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke sharing');
      throw error;
    }
  };

  // Update sharing permissions for a category
  const updateCategoryPermissions = async (parentId, category, settings) => {
    try {
      await SharingService.updateSharingPermissions(parentId, category, settings);
      toast.success('Permissions updated');
      await loadSharingData();
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update permissions');
      throw error;
    }
  };

  // Add parent comment
  const addComment = async (sharingPermissionId, commentText) => {
    try {
      await SharingService.addParentComment(sharingPermissionId, commentText);
      toast.success('Comment added');
      await loadSharingData();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      throw error;
    }
  };

  // Mark notifications as read
  const markNotificationsRead = async (notificationIds) => {
    try {
      await SharingService.markNotificationsAsRead(notificationIds);
      await loadNotifications();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  // Get available parents to share with
  const getAvailableParents = async () => {
    try {
      // Get current user's family_member record first to find their family
      const { data: currentMember } = await supabase
        .from('family_members')
        .select('id, family_id')
        .eq('auth_user_id', currentUser?.id)
        .single();

      if (currentMember?.family_id) {
        // Get all parents in the same family
        const { data, error } = await supabase
          .from('family_members')
          .select('*')
          .eq('family_id', currentMember.family_id)
          .eq('role', 'parent');

        if (error) {
          console.error('Error fetching parents:', error);
          return [];
        }

        return data || [];
      } else {
        // Fallback: get all parents if no family association found
        const { data, error } = await supabase
          .from('family_members')
          .select('*')
          .eq('role', 'parent');

        if (error) {
          console.error('Error fetching parents:', error);
          return [];
        }

        return data || [];
      }
    } catch (error) {
      console.error('Error in getAvailableParents:', error);
      return [];
    }
  };

  // Check if an item is shared
  const isItemShared = (itemType, itemId) => {
    if (!sharedItems[itemType]) return false;
    return sharedItems[itemType].some(item =>
      item.item_id === itemId && !item.revoked_at
    );
  };

  // Get share status for an item
  const getShareStatus = (itemType, itemId) => {
    if (!sharedItems[itemType]) return null;
    const item = sharedItems[itemType].find(i => i.item_id === itemId);
    if (!item) return null;

    return {
      isShared: !item.revoked_at,
      sharedWith: item.shared_with || [],
      sharedAt: item.shared_at,
      hasComments: item.comments?.count > 0,
      version: item.version
    };
  };

  const value = {
    // State
    sharedItems,
    parentSharedItems,
    sharingPermissions,
    parentAccess,
    notifications,
    unreadCount,
    loading,
    isParent,

    // Actions
    shareItem,
    revokeSharing,
    updateCategoryPermissions,
    addComment,
    markNotificationsRead,
    loadSharingData,
    loadNotifications,

    // Helpers
    getAvailableParents,
    isItemShared,
    getShareStatus
  };

  return (
    <SharingContext.Provider value={value}>
      {children}
    </SharingContext.Provider>
  );
};